import React from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { ContactEditForm } from '../components/ContactEditForm';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface ContactCreateScreenProps {
  /** When present, the newly-created contact is linked to this System
   * Datalist (via DataListSystemMembership) and the screen redirects back to
   * that list's records instead of the normal Contacts screen — reached via
   * DataListRecordsScreen's "Add" button for a contacts-type system list. */
  listId?: string;
}

/**
 * Routed, full-screen wrapper around ContactEditForm (the real, reusable
 * contact form — not WhatsAppContactModal, which hardcodes its own Modal
 * chrome and WhatsApp-specific message-limit logic). Contacts has no
 * dedicated routed "add contact" screen otherwise — the core Contacts screen
 * opens ContactEditForm inline/as a modal instead, which doesn't fit here
 * since Data List Records is a separate routed screen.
 */
export const ContactCreateScreen: React.FC<ContactCreateScreenProps> = ({ listId }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const handleSave = async (contact: { id: string }) => {
    if (listId) {
      try {
        await apiService.linkDataListRecords(listId, [contact.id]);
      } catch (err) {
        console.error('[ContactCreateScreen] Failed to link new contact to data list:', err);
      }
      navigateTo('data-list-records', listId);
    } else {
      navigateTo('contacts');
    }
  };

  const handleClose = () => {
    if (listId) {
      navigateTo('data-list-records', listId);
    } else {
      navigateTo('contacts');
    }
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      formHeader={<FormHeader title="Add Contact" onBack={handleClose} />}
    >
      <View style={styles.container}>
        <ContactEditForm contact={null} onSave={handleSave as any} onClose={handleClose} />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },
});
